import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { getAppI18nConfig, initI18n } from 'cfgi18n';
import i18next from 'i18next';
import { AppProps } from 'next/app';
import Head from 'next/head';
import { useEffect, useState } from 'react';
import { I18nextProvider } from 'react-i18next';

import { useBrowsing } from 'hooks/use-browsing';

import { Layout } from '@home/layout';

import './styles.css';

function makeQueryClient() {
	return new QueryClient({
		defaultOptions: {
			queries: {
				refetchOnWindowFocus: false,
			},
		},
	});
}

function CustomApp({
	Component,
	pageProps,
}: Pick<
	AppProps<{ locale: string; remoteStylesheets?: string[] }>,
	'Component' | 'pageProps'
>) {
	const locale = pageProps.locale || 'en';
	const { pathname } = useBrowsing();
	const isErrorPage = pathname === '/_error';
	// Per-app instance: a module singleton shares cache across SSR requests
	// and can leak dehydrated state between navigations.
	const [queryClient] = useState(makeQueryClient);

	// i18n initialises in the background: rendering must not be gated on it,
	// otherwise the statically generated HTML is replaced by a blank page
	// until the client-side config fetch resolves. react-i18next consumers
	// re-render on their own once initialisation completes.
	useEffect(() => {
		const setup = async () => {
			const { supportedLocales } = await getAppI18nConfig('home');
			const i18n = await initI18n(locale, supportedLocales);
			i18n.options.nsSeparator = false;
		};

		void setup();
	}, [locale]);

	return (
		<>
			<Head>
				{pageProps.remoteStylesheets?.map((href) => (
					<link key={href} rel="stylesheet" href={href} />
				))}
				<title>Manchester City Football Club</title>
				<meta
					name="viewport"
					content="width=device-width, initial-scale=1.0"
				/>
				<meta
					name="description"
					content="The Official Website of Manchester City F.C. Get the latest Man City news, injury updates, fixtures, player signings, match highlights &amp; much more!"
				/>
				<meta
					name="theme-color"
					content="var(--colour-background-action-brand-active)"
				/>
				<link rel="icon" href="/favicon.ico" />
				<link rel="apple-touch-icon" href="/images/icons-192.png" />
				<link rel="manifest" href="/manifest.json" />
			</Head>

			<main className="app">
				<QueryClientProvider client={queryClient}>
					<I18nextProvider i18n={i18next}>
						{isErrorPage ? (
							<Component {...pageProps} />
						) : (
							<Layout locale={locale}>
								<Component {...pageProps} />
							</Layout>
						)}
					</I18nextProvider>
					<ReactQueryDevtools initialIsOpen={false} />
				</QueryClientProvider>
			</main>
		</>
	);
}

export default CustomApp;
